import React, { useState, useEffect } from 'react';
import { getLots, getWeeks, BUSINESS_UNITS } from '../../services/api';

const AREAS = [
  { id: 1, name: 'Producción', label: 'Lote' },
  { id: 2, name: 'Empaque', label: 'Línea' },
];

// function formatDate(dateStr) {
//   if (!dateStr) return '';
//   const d = new Date(dateStr);
//   return d.toLocaleDateString('es-MX', { day: '2-digit', month: 'short', year: 'numeric' });
// }

function formatDate(dateStr) {
  if (!dateStr) return '';

  const [year, month, day] = dateStr.split('-');
  const d = new Date(year, month - 1, day);

  return d.toLocaleDateString('es-MX', {
    day: '2-digit',
    month: 'short',
    year: 'numeric'
  });
}

export default function NewPlanningModal({ onClose, onCreated }) {
  const [step, setStep] = useState(1);
  const [form, setForm] = useState({
    businessUnitId: '',
    areaId: '',
    lotId: '',
    weekId: '',
  });
  const [lots, setLots] = useState([]);
  const [weeks, setWeeks] = useState([]);
  const [loadingLots, setLoadingLots] = useState(false);
  const [loadingWeeks, setLoadingWeeks] = useState(false);
  const [error, setError] = useState('');

  const set = (key, val) => setForm(f => ({ ...f, [key]: val }));

  // Load lots & weeks when business unit or area changes
  useEffect(() => {
    if (form.businessUnitId && form.areaId) {
      setLots([]);
      set('lotId', '');
      setLoadingLots(true);
      getLots(form.businessUnitId)
        .then(data => {
          const list = Array.isArray(data) ? data : data.data || [];
          // Filter by lot_type if needed: 'lot' = Producción, 'line' = Empaque
          setLots(list);
        })
        .catch(() => setLots([]))
        .finally(() => setLoadingLots(false));
    }
  }, [form.businessUnitId, form.areaId]);

  useEffect(() => {
    if (form.businessUnitId) {
      setWeeks([]);
      set('weekId', '');
      setLoadingWeeks(true);
      getWeeks(form.businessUnitId)
        .then(data => {
          const list = Array.isArray(data) ? data : data.data || [];
          setWeeks(list);
        })
        .catch(() => setWeeks([]))
        .finally(() => setLoadingWeeks(false));
    }
  }, [form.businessUnitId]);

  const selectedLot = lots.find(l => String(l.id) === String(form.lotId));
  const selectedWeek = weeks.find(w => String(w.id) === String(form.weekId));
  const selectedBU = BUSINESS_UNITS.find(b => String(b.id) === String(form.businessUnitId));
  const selectedArea = AREAS.find(a => String(a.id) === String(form.areaId));

  const canCreate = form.businessUnitId && form.areaId && form.lotId && form.weekId;

  const handleCreate = () => {
    if (!canCreate) { setError('Completa todos los campos'); return; }
    onCreated({
      businessUnitId: Number(form.businessUnitId),
      areaId: Number(form.areaId),
      lotId: Number(form.lotId),
      weekId: Number(form.weekId),
      lot: selectedLot,
      week: selectedWeek,
      businessUnit: selectedBU,
      area: selectedArea,
    });
  };

  return (
    <div className="modal-overlay" onClick={e => e.target === e.currentTarget && onClose()}>
      <div className="modal-box" style={{ maxWidth: 500 }}>
        <div className="modal-header">
          <h2>
            <span style={{ color: 'var(--accent)', marginRight: 8 }}>+</span>
            Nueva Planeación
          </h2>
          <button className="btn btn-ghost btn-sm" onClick={onClose} style={{ padding: '4px 8px' }}>
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/>
            </svg>
          </button>
        </div>

        <div className="modal-body">
          {error && (
            <div className="alert alert-error" style={{ marginBottom: 16 }}>
              {error}
            </div>
          )}

          {/* Business Unit */}
          <div className="form-group">
            <label className="form-label">Unidad de negocio</label>
            <select
              className="select-field"
              value={form.businessUnitId}
              onChange={e => { set('businessUnitId', e.target.value); set('areaId', ''); set('lotId', ''); set('weekId', ''); }}
            >
              <option value="">Selecciona una unidad de negocio</option>
              {BUSINESS_UNITS.map(b => (
                <option key={b.id} value={b.id}>{b.name}</option>
              ))}
            </select>
          </div>

          {/* Area */}
          <div className="form-group">
            <label className="form-label">Área</label>
            <div style={{ display: 'flex', gap: 10 }}>
              {AREAS.map(a => (
                <button
                  key={a.id}
                  type="button"
                  onClick={() => { set('areaId', a.id); set('lotId', ''); }}
                  disabled={!form.businessUnitId}
                  style={{
                    flex: 1,
                    padding: '12px',
                    borderRadius: 'var(--radius)',
                    border: `1px solid ${String(form.areaId) === String(a.id) ? 'var(--accent)' : 'var(--border-light)'}`,
                    background: String(form.areaId) === String(a.id) ? 'var(--accent-glow)' : 'var(--bg-primary)',
                    color: String(form.areaId) === String(a.id) ? 'var(--accent)' : 'var(--text-secondary)',
                    cursor: form.businessUnitId ? 'pointer' : 'not-allowed',
                    fontFamily: 'var(--font-main)',
                    fontWeight: 600,
                    fontSize: 14,
                    transition: 'var(--transition)',
                    opacity: form.businessUnitId ? 1 : 0.5,
                  }}
                >
                  {a.name}
                </button>
              ))}
            </div>
          </div>

          {/* Lot / Line */}
          <div className="form-group">
            <label className="form-label">
              {selectedArea ? selectedArea.label : 'Lote / Línea'}
            </label>
            {loadingLots ? (
              <div style={{ padding: '10px 0', color: 'var(--text-muted)', fontSize: 13 }}>Cargando...</div>
            ) : (
              <select
                className="select-field"
                value={form.lotId}
                onChange={e => set('lotId', e.target.value)}
                disabled={!form.areaId || lots.length === 0}
              >
                <option value="">
                  {!form.areaId ? 'Selecciona un área primero' : lots.length === 0 ? 'Sin opciones' : `Selecciona un ${selectedArea?.label?.toLowerCase() || 'lote'}`}
                </option>
                {lots.map(l => (
                  <option key={l.id} value={l.id}>
                    {l.name}{l.crop_name ? ` — ${l.crop_name}` : ''}
                  </option>
                ))}
              </select>
            )}
          </div>

          {/* Week */}
          <div className="form-group">
            <label className="form-label">Semana</label>
            {loadingWeeks ? (
              <div style={{ padding: '10px 0', color: 'var(--text-muted)', fontSize: 13 }}>Cargando semanas...</div>
            ) : (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 6, maxHeight: 200, overflowY: 'auto' }}>
                {weeks.length === 0 && (
                  <div style={{ color: 'var(--text-muted)', fontSize: 13 }}>
                    {!form.businessUnitId ? 'Selecciona una unidad de negocio primero' : 'Sin semanas disponibles'}
                  </div>
                )}
                {weeks.map(w => {
                  const selected = String(form.weekId) === String(w.id);
                  return (
                    <button
                      key={w.id}
                      type="button"
                      onClick={() => set('weekId', w.id)}
                      style={{
                        padding: '10px 14px',
                        borderRadius: 'var(--radius)',
                        border: `1px solid ${selected ? 'var(--accent)' : 'var(--border-light)'}`,
                        background: selected ? 'var(--accent-glow)' : 'var(--bg-primary)',
                        cursor: 'pointer',
                        textAlign: 'left',
                        fontFamily: 'var(--font-main)',
                        transition: 'var(--transition)',
                      }}
                    >
                      <div style={{ fontWeight: 600, fontSize: 13, color: selected ? 'var(--accent)' : 'var(--text-primary)' }}>
                        {w.name}
                      </div>
                      <div style={{ fontSize: 11, color: 'var(--text-muted)', marginTop: 2 }}>
                        {formatDate(w.from_date)} — {formatDate(w.to_date)}
                      </div>
                    </button>
                  );
                })}
              </div>
            )}
          </div>
        </div>

        <div className="modal-footer">
          <button className="btn btn-secondary" onClick={onClose}>Cancelar</button>
          <button className="btn btn-primary" onClick={handleCreate} disabled={!canCreate}>
            Continuar
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
              <polyline points="9 18 15 12 9 6"/>
            </svg>
          </button>
        </div>
      </div>
    </div>
  );
}
